<template>
<page-wrapper>
  <v-data-table v-if="!ordering" :headers="headers" :items="websiteProjects"
                :items-per-page="50" :footer-props="{ itemsPerPageOptions: [25, 50, 100, -1] }"
                :loading="loading">
    <template v-slot:top>
      <v-toolbar flat>
        <v-toolbar-title class="title">Website Projects</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text class="mr-2" @click="startOrdering">Change order</v-btn>
        <v-btn color="warning" @click="statusOpen = true">Status</v-btn>
      </v-toolbar>
    </template>
    <template v-slot:item.job_number="{ item }">
      <span v-if="item.job_number">{{ item.job_number }}</span>
    </template>
    <template v-slot:item.sync="{ item }">
      <v-icon small :color="item.sync ? 'green' : 'grey lighten-2'">
        {{ item.sync ? 'mdi-check-circle' : 'mdi-circle-outline' }}
      </v-icon>
    </template>
    <template v-slot:item.featured="{ item }">
      <v-icon v-if="item.featured" small color="indigo darken-1">mdi-star</v-icon>
    </template>
    <template v-slot:item.edit="{ item }">
      <v-icon small @click="editItem(item)">mdi-pencil</v-icon>
    </template>
  </v-data-table>

  <div v-else-if="ordering">
    <v-toolbar flat>
      <v-toolbar-title class="title">Website Projects Order</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-progress-circular v-if="working" indeterminate size="20" color="indigo" />
      <template v-else>
        <v-btn text small @click="ordering = false">Cancel</v-btn>
        <v-btn text small color="primary" @click="saveOrder">Save order</v-btn>
      </template>
    </v-toolbar>

    <table class="order-table">
      <thead>
        <tr>
          <th></th>
          <th>ID</th>
          <th>Title</th>
          <th>Client</th>
          <th>Year</th>
          <th></th>
        </tr>
      </thead>
      <draggable v-model="orderedProjects" tag="tbody" handle=".drag-handle">
        <tr v-for="item in orderedProjects" :key="item.id">
          <td><v-icon class="drag-handle" small>mdi-drag</v-icon></td>
          <td class="grey--text">{{ item.id }}</td>
          <td>{{ item.title }}</td>
          <td class="grey--text">{{ item.client }}</td>
          <td class="grey--text">{{ item.year }}</td>
          <td><v-icon small @click="sendToTop(item)">mdi-arrow-up</v-icon></td>
        </tr>
      </draggable>
    </table>
  </div>

  <v-dialog v-model="editing" max-width="800px">
    <edit-website-project ref="editor" @close="onClose"></edit-website-project>
  </v-dialog>

  <v-dialog v-model="statusOpen" max-width="600px" eager>
    <website-status :open="statusOpen" @close="statusOpen = false"></website-status>
  </v-dialog>
</page-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import * as api from '../api'
import PageWrapper from '../components/ui/PageWrapper'
import EditWebsiteProject from '../components/website/EditWebsiteProject'
import WebsiteStatus from '../components/website/WebsiteStatus.vue'

export default {
  name: 'WebsiteProjects',
  components: { WebsiteStatus, PageWrapper, EditWebsiteProject, draggable },
  props: {
    editId: { type: Number, default: null }
  },
  data() {
    return {
      loading: true,
      editing: false,
      ordering: false,
      working: false,
      orderedProjects: [],
      statusOpen: false,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Job', value: 'job_number' },
        { text: 'Title', value: 'title' },
        { text: 'Client', value: 'client' },
        { text: 'Year', value: 'year' },
        { text: '', value: 'sync' },
        { text: '', value: 'featured' },
        { text: '', value: 'edit', sortable: false }
      ]
    }
  },
  computed: {
    ...mapState(['websiteProjects'])
  },
  methods: {
    editItem(item) {
      this.$router.replace({ name: 'website-projects', params: { id: item.id } }).catch(() => {})
      this.editing = true
      this.$nextTick(() => { this.$refs.editor.setProject(item) })
    },
    onClose() {
      this.editing = false
      this.$router.replace({ name: 'website-projects' }).catch(() => {})
    },
    startOrdering() {
      this.orderedProjects = this.websiteProjects.sort((a, b) => b.order - a.order)
      this.ordering = true
    },
    sendToTop(item) {
      this.orderedProjects = [item, ...this.orderedProjects.filter(p => p.id !== item.id)]
    },
    async saveOrder() {
      this.working = true
      await api.putJson('/website/projects/order', this.orderedProjects.map(p => p.id))
      await this.$store.dispatch('getWebsiteProjects', true)
      this.working = false
      this.ordering = false
    }
  },
  async mounted() {
    await this.$store.dispatch('getWebsiteProjects')
    this.loading = false
    if (this.editId) {
      const project = this.websiteProjects.find(p => p.id === this.editId)
      if (project) this.editItem(project)
    }
  }
}
</script>

<style scoped lang="scss">
.order-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #888;
    padding: 6px 12px;
    border-bottom: 1px solid #e0e0e0;
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 0.875rem;
  }

  .drag-handle {
    cursor: grab;
    &:active { cursor: grabbing; }
  }
}
</style>
